<template>
    <span ref="trigger" @mouseenter="show" @mouseleave="hide" @focus="show" @blur="hide">
        <slot />
    </span>

    <teleport to="body">
        <div v-if="visible" ref="tooltipEl" :style="tooltipStyle" :class="['custom-tooltip', placementClass]"
            role="tooltip" aria-hidden="false">
            {{ text }}
        </div>
    </teleport>
</template>

<script setup>
import { ref, nextTick, onBeforeUnmount, defineProps } from 'vue'

const props = defineProps({
    text: { type: String, default: '' },
    placement: { type: String, default: 'bottom' }, // right (default), left, top, bottom
    offset: { type: Number, default: 6 }
})

const visible = ref(false)
const trigger = ref(null)
const tooltipEl = ref(null)
const tooltipStyle = ref({})
const placementClass = ref('placement-right')

function updatePosition() {
    const el = trigger.value
    if (!el) return
    const r = el.getBoundingClientRect()
    nextTick(() => {
        const base = { position: 'fixed', zIndex: '2147483647', pointerEvents: 'none' }
        tooltipStyle.value = { ...base, top: `${r.top}px`, left: `${r.right + props.offset}px` }
        const tip = tooltipEl.value
        if (!tip) return
        const t = tip.getBoundingClientRect()
        const padding = 8

        const setRight = () => {
            const top = r.top + r.height / 2
            const left = r.right + props.offset
            tooltipStyle.value = { ...base, top: `${top}px`, left: `${left}px` }
            placementClass.value = 'placement-right'
        }
        const setLeft = () => {
            const top = r.top + r.height / 2 - t.height / 2
            const left = r.left - props.offset - t.width
            tooltipStyle.value = { ...base, top: `${top}px`, left: `${left}px` }
            placementClass.value = 'placement-left'
        }
        const setTop = () => {
            const top = r.top - props.offset - t.height
            const left = r.left + r.width / 2 - t.width / 2
            tooltipStyle.value = { ...base, top: `${top}px`, left: `${left}px` }
            placementClass.value = 'placement-top'
        }
        const setBottom = () => {
            const top = r.bottom + props.offset
            const left = r.left + r.width / 2 - t.width / 2
            tooltipStyle.value = { ...base, top: `${top}px`, left: `${left}px` }
            placementClass.value = 'placement-bottom'
        }

        if (props.placement === 'left') setLeft()
        else if (props.placement === 'top') setTop()
        else if (props.placement === 'bottom') setBottom()
        else setRight()

        const after = tooltipEl.value?.getBoundingClientRect()
        if (!after) return

        if (placementClass.value === 'placement-right' || placementClass.value === 'placement-left') {
            if (after.right > window.innerWidth - padding) setLeft()
            if (after.left < padding) setRight()
        } else {
            if (after.top < padding) setBottom()
            if (after.bottom > window.innerHeight - padding) setTop()
        }
    })
}

function show() {
    visible.value = true
    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
}

function hide() {
    visible.value = false
    window.removeEventListener('scroll', updatePosition, true)
    window.removeEventListener('resize', updatePosition)
}

onBeforeUnmount(() => {
    window.removeEventListener('scroll', updatePosition, true)
    window.removeEventListener('resize', updatePosition)
})
</script>

<style>
.custom-tooltip {
    background: #94A4B9;
    color: #fff;
    padding: 6px 8px;
    border-radius: 4px;
    font-size: 12px;
    transform-origin: center;
    transition: opacity .12s ease;
    max-width: 310px;
    white-space: normal;
    line-height: 1.3;
    border: 0.4px solid #959EAA;
    position: fixed;
    overflow-wrap: anywhere;
    word-break: break-word;  
}

.custom-tooltip::before,
.custom-tooltip::after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
}

.placement-right::before {
    border-width: 7px;
    border-style: solid;
    border-color: transparent #959EAA transparent transparent;
    left: -14px;
    top: 50%;
    transform: translateY(-50%);
}

.placement-right::after {
    border-width: 6px;
    border-style: solid;
    border-color: transparent #94A4B9 transparent transparent;
    left: -12px;
    top: 50%;
    transform: translateY(-50%);
}

.placement-left::before {
    border-width: 7px;
    border-style: solid;
    border-color: transparent transparent transparent #959EAA;
    right: -14px;
    top: 50%;
    transform: translateY(-50%);
}

.placement-left::after {
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent transparent #94A4B9;
    right: -12px;
    top: 50%;
    transform: translateY(-50%);
}

.placement-top::before {
    border-width: 7px;
    border-style: solid;
    border-color: #959EAA transparent transparent transparent;
    bottom: -14px;
    left: 50%;
    transform: translateX(-50%);
}

.placement-top::after {
    border-width: 6px;
    border-style: solid;
    border-color: #94A4B9 transparent transparent transparent;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
}

.placement-bottom::before {
    border-width: 7px;
    border-style: solid;
    border-color: transparent transparent #959EAA transparent;
    top: -14px;
    left: 50%;
    transform: translateX(-50%);
}

.placement-bottom::after {
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent #94A4B9 transparent;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
}
</style>